import { useEffect, useMemo, useState } from 'react';
import { cmsService } from '@/services';
import { getPageDefaults } from '@/content/pageDefaults';
import { mergePageContent } from '@/utils/mergePageContent';

const inIframe = () => {
  try { return window.self !== window.top; } catch { return true; }
};

/**
 * Hook per i contenuti editoriali di una pagina pubblica.
 *
 * - Carica l'override salvato dal CRM (`/api/cms/page/{slug}`).
 * - Se la pagina e' aperta dentro l'anteprima dell'editor (iframe), ascolta i
 *   messaggi `postMessage` con la bozza live e la applica in tempo reale.
 *
 * Ritorna l'oggetto "override vivo": override salvato con sopra, se presente, la
 * bozza live. Le pagine mantengono i propri fallback (`|| default`), percio' il
 * valore puo' essere usato esattamente come il vecchio stato `ov`.
 *
 * @param {string} slug
 * @returns {Object} contenuto da usare come override (`ov`)
 */
export function useLivePageContent(slug) {
  const [ov, setOv] = useState({});
  const [draft, setDraft] = useState(null);

  useEffect(() => {
    let alive = true;
    cmsService.getPageContent(slug)
      .then((c) => { if (alive) setOv(c || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [slug]);

  useEffect(() => {
    if (!inIframe()) return undefined;
    // Origine attesa del parent (editor): stesso deployment del sito pubblico.
    const expectedOrigin = window.location.origin;
    const handler = (e) => {
      // Accetta la bozza SOLO dal parent previsto e dall'origine esatta.
      if (e.origin !== expectedOrigin) return;
      if (e.source !== window.parent) return;
      const m = e.data;
      if (!m || typeof m !== 'object') return;
      if (m.type !== 'cms.preview' || m.slug !== slug) return;
      if (!m.draft || typeof m.draft !== 'object' || Array.isArray(m.draft)) return;
      const next = m.draft;
      setDraft((prev) => {
        try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch { /* noop */ }
        return next;
      });
    };
    window.addEventListener('message', handler);
    // Handshake: avvisa l'editor (parent, origine esatta) che siamo pronti a ricevere la bozza.
    try { window.parent.postMessage({ type: 'cms.preview.ready', slug }, expectedOrigin); } catch { /* noop */ }
    return () => window.removeEventListener('message', handler);
  }, [slug]);

  return useMemo(() => (draft !== null ? { ...(ov || {}), ...draft } : (ov || {})), [ov, draft]);
}

/**
 * Variante "pronta all'uso": ritorna direttamente il contenuto effettivo
 * (default del registro + override + bozza live) gia' unito campo-per-campo.
 * Utile per pagine che non hanno gia' i propri fallback inline.
 */
export function usePageContent(slug) {
  const live = useLivePageContent(slug);
  const defaults = useMemo(() => getPageDefaults(slug), [slug]);
  return useMemo(() => mergePageContent(defaults, live, null), [defaults, live]);
}

export default useLivePageContent;
